import React, { useState, useEffect } from 'react';
import SearchFilters, {
  DEFAULT_GROUP_ACTIVITY_QUERY,
  DEFAULT_ITEM_PER_PAGE,
  DEFAULT_PAGE_NUMBER
} from '../../components/ActivityLog/Filtering/SearchFilters';
import { ActivityTable } from '../../components/ActivityLog/Table/ActivityTable';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Modal from '../../components/Modal/NewModal';
import ActivityDetail from '../../components/ActivityLog/ActivityDetail/ActivityDetail';
import { useActivities } from '../../hooks/useActivities';
import { PageSpinner, ReactTableContextProvider } from '@td-client-frontend/common';
import { Download } from '@td-client-frontend/icons';
import { useDownloadActivities } from '../../hooks/useDownloadActivities';
import Pagination, { ItemsPerPageOption } from '../../components/Pagination';
import { PartialActivityLogSearchQuery } from '../../@types/Audit';

function ActivityLog() {
  const notEmptyAttributes = (values: object) => Object.fromEntries(Object.entries(values).filter(([_, v]) => v));
  const getSearchParameter = (query: string, name: string) => new URLSearchParams(query).get(name);

  const { search } = useLocation();
  const employerName = getSearchParameter(search, 'employerName')?.toLowerCase();

  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPageOption>(DEFAULT_ITEM_PER_PAGE);
  const [page, setSelectedPage] = useState<number>(DEFAULT_PAGE_NUMBER);
  const [query, setQuery] = useState<PartialActivityLogSearchQuery>({
    ...DEFAULT_GROUP_ACTIVITY_QUERY,
    ...notEmptyAttributes({ employerName })
  });
  const { data, isLoading } = useActivities(query);

  const handleDownloadActivities = useDownloadActivities({ ...query, totalPages: data?.totalPages });

  // Router Props
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const activityId = params?.id;

  const resetPagination = () => {
    setSelectedPage(DEFAULT_PAGE_NUMBER);
    setItemsPerPage(DEFAULT_ITEM_PER_PAGE);
  };

  const searchHandler = (values: PartialActivityLogSearchQuery) => {
    resetPagination();
    setQuery(values);
  };

  const searchResetHandler = () => {
    resetPagination();
    setQuery(DEFAULT_GROUP_ACTIVITY_QUERY);
  };

  const toggleModal = () => {
    history.goBack();
  };

  useEffect(() => {
    setQuery((prev) => ({ ...prev, itemPerPage: itemsPerPage.value, page }));
  }, [itemsPerPage, page]);

  return (
    <>
      {activityId && (
        <Modal
          isOpen={!!activityId ?? false}
          onClose={toggleModal}
          contentLabel="Activity Detail Modal"
          title="Activity Detail Modal"
          header="Activity Detail"
          size="large"
        >
          <ActivityDetail id={activityId ?? false} type="group" />
        </Modal>
      )}
      <section
        data-testid="ActivityLogsPage"
        className="flex flex-col w-full px-6 py-4 mx-auto bg-white sm:items-baseline space-y-7"
      >
        <div className="flex flex-col space-y-7 w-full">
          <h1 className="text-4xl font-effra">Groups activity log</h1>
          <SearchFilters initialData={query} onSearch={searchHandler} onSearchReset={searchResetHandler} />
        </div>
        <div className="flex flex-col w-full mb-40">
          {isLoading ? <PageSpinner size={80} /> : <ActivityTable data={data?.records || []} />}
          {data?.totalPages! > 1 && (
            <Pagination
              totalPages={data?.totalPages}
              onItemsPerPageChange={setItemsPerPage}
              onPageChange={setSelectedPage}
              selectedPage={page}
              itemsPerPage={itemsPerPage}
              isLoading={isLoading}
            />
          )}
        </div>
        {data?.records && data.records.length > 0 && (
          <button
            className="flex items-center gap-2 self-end text-primary-500 font-medium cursor-pointer"
            onClick={handleDownloadActivities}
            data-testid="download-all-activities"
          >
            <Download className="fill-current text-primary-500" />
            Download all activities
          </button>
        )}
      </section>
    </>
  );
}

export default function ActivityLogsWithProvider() {
  return (
    <ReactTableContextProvider initialState={{ state: { sortBy: [{ id: 'Date', desc: false }] } }}>
      <ActivityLog />
    </ReactTableContextProvider>
  );
}
